import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Edit as EditIcon,
  Eye,
  Loader2,
  Plus,
  Send,
  Trash2,
  X,
} from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import { api, extractErrorMessage } from '../../../lib/api';
import { ModalPortal } from '../../../components/ModalPortal';

/**
 * Onglet "Modèles emails" — US-ADM-07.
 *
 *  - Liste des templates (code, nom, catégorie, actif).
 *  - Modal édition avec Quill (WYSIWYG) + sélecteur de variables.
 *  - Prévisualisation rendue (Handlebars) avec dataset fictif.
 *  - Envoi de test à une adresse arbitraire.
 *
 * Variables disponibles fournies par `GET /admin/tenant/email/variables`.
 */

interface TemplateRow {
  id: string;
  code: string;
  nom: string;
  description: string | null;
  categorie: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface TemplateDetail extends TemplateRow {
  sujet: string;
  contenu_html: string;
}

const CATEGORIES = ['transactionnel', 'notification', 'marketing'] as const;

export function EmailTemplatesTab() {
  const [items, setItems] = useState<TemplateRow[]>([]);
  const [variables, setVariables] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [editing, setEditing] = useState<TemplateRow | 'new' | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [list, vars] = await Promise.all([
          api.get<{ data: TemplateRow[] }>('/admin/email-templates'),
          api.get<{ data: Record<string, string[]> }>('/admin/tenant/email/variables'),
        ]);
        setItems(list.data.data);
        setVariables(vars.data.data);
      } catch (err) {
        setErrorMessage(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function refresh() {
    const res = await api.get<{ data: TemplateRow[] }>('/admin/email-templates');
    setItems(res.data.data);
  }

  async function handleDelete(t: TemplateRow) {
    if (!confirm(`Supprimer le template "${t.nom}" ?`)) return;
    try {
      await api.delete(`/admin/email-templates/${t.id}`);
      refresh();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Modèles d'emails dynamiques avec variables Handlebars (
          <code className="font-mono text-xs">{`{{user.first_name}}`}</code>).
        </p>
        <button
          onClick={() => setEditing('new')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau modèle
        </button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-12 text-sm text-gray-500 rounded-lg border-2 border-dashed border-gray-200">
          Aucun modèle pour l'instant. Les templates par défaut (HTML inline) seront utilisés
          jusqu'à ce que vous créiez les vôtres.
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Nom
                </th>
                <th className="hidden md:table-cell px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Code
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Catégorie
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Statut
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">{t.nom}</td>
                  <td className="hidden md:table-cell px-4 py-3 text-xs font-mono text-gray-700">
                    {t.code}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                      {t.categorie}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {t.is_active ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Actif
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        Inactif
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                    <button
                      onClick={() => setEditing(t)}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                      <EditIcon className="w-3.5 h-3.5 mr-1" />
                      Éditer
                    </button>
                    <button
                      onClick={() => handleDelete(t)}
                      className="inline-flex items-center p-1.5 text-red-600 hover:bg-red-50 rounded"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <TemplateEditorModal
          template={editing === 'new' ? null : editing}
          variables={variables}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal éditeur Quill + preview + test
// ─────────────────────────────────────────────────────────────────────────────

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ color: [] }, { background: [] }],
    ['link', 'clean'],
  ],
};

function TemplateEditorModal({
  template,
  variables,
  onClose,
  onSaved,
}: {
  template: TemplateRow | null;
  variables: Record<string, string[]>;
  onClose(): void;
  onSaved(): void;
}) {
  const isCreate = !template;
  const [code, setCode] = useState(template?.code ?? '');
  const [nom, setNom] = useState(template?.nom ?? '');
  const [description, setDescription] = useState(template?.description ?? '');
  const [categorie, setCategorie] = useState(template?.categorie ?? 'transactionnel');
  const [isActive, setIsActive] = useState(template?.is_active ?? true);
  const [sujet, setSujet] = useState('');
  const [contenuHtml, setContenuHtml] = useState('');
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [testTo, setTestTo] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  const subjectInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!template) return;
    (async () => {
      try {
        const res = await api.get<{ data: TemplateDetail }>(
          `/admin/email-templates/${template.id}`,
        );
        setSujet(res.data.data.sujet);
        setContenuHtml(res.data.data.contenu_html);
      } catch (err) {
        setErrorMessage(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [template]);

  // Liste à plat des variables connues — utilisé par le sélecteur d'insertion.
  const allVariables = useMemo(() => {
    const set = new Set<string>();
    for (const arr of Object.values(variables)) for (const v of arr) set.add(v);
    return Array.from(set).sort();
  }, [variables]);

  function insertVariable(varName: string) {
    const handle = `{{${varName}}}`;
    // Si le focus est sur le champ sujet, on insère là — sinon dans le HTML.
    if (document.activeElement === subjectInputRef.current) {
      const el = subjectInputRef.current!;
      const start = el.selectionStart ?? sujet.length;
      const end = el.selectionEnd ?? sujet.length;
      setSujet(sujet.slice(0, start) + handle + sujet.slice(end));
    } else {
      setContenuHtml(contenuHtml + handle);
    }
  }

  async function generatePreview() {
    setErrorMessage(null);
    try {
      // Dataset fictif simple — l'admin pourra l'enrichir.
      const fakeVars = buildFakeVariables();
      const res = await api.post<{ data: { subject: string; html: string } }>(
        '/admin/email-templates/preview',
        { sujet, contenu_html: contenuHtml, variables: fakeVars },
      );
      setPreviewSubject(res.data.data.subject);
      setPreviewHtml(res.data.data.html);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErrorMessage(null);
    try {
      const payload = {
        code,
        nom,
        description: description || null,
        categorie,
        sujet,
        contenu_html: contenuHtml,
        is_active: isActive,
      };
      if (template) {
        await api.patch(`/admin/email-templates/${template.id}`, payload);
      } else {
        await api.post('/admin/email-templates', payload);
      }
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function sendTest() {
    if (!testTo) return;
    setTestSending(true);
    setTestResult(null);
    setErrorMessage(null);
    try {
      const res = await api.post<{ data: { provider: string } }>('/admin/tenant/email/test', {
        to: testTo,
        sujet,
        contenu_html: contenuHtml,
        variables: buildFakeVariables(),
      });
      setTestResult(`Envoyé via ${res.data.data.provider}`);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setTestSending(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-5xl w-full shadow-2xl max-h-[95vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900">
              {isCreate ? 'Nouveau modèle email' : `Éditer : ${template!.nom}`}
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {errorMessage && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
              </div>
            ) : (
              <form id="template-form" onSubmit={onSave} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Field label="Code (snake_case) *">
                    <input
                      required
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      pattern="[a-z][a-z0-9_]*"
                      className={inputClass + ' font-mono text-xs'}
                    />
                  </Field>
                  <Field label="Nom affiché *" className="md:col-span-2">
                    <input
                      required
                      value={nom}
                      onChange={(e) => setNom(e.target.value)}
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Description" className="md:col-span-2">
                    <input
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Catégorie">
                    <select
                      value={categorie}
                      onChange={(e) => setCategorie(e.target.value)}
                      className={inputClass}
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <Field label="Sujet (Handlebars supporté) *">
                  <input
                    ref={subjectInputRef}
                    required
                    value={sujet}
                    onChange={(e) => setSujet(e.target.value)}
                    placeholder='ex. "Bienvenue {{user.first_name}}"'
                    className={inputClass}
                  />
                </Field>

                {/* Variable picker */}
                <details className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700">
                    Variables disponibles (cliquez pour insérer)
                  </summary>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {allVariables.map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => insertVariable(v)}
                        className="px-2 py-1 text-xs font-mono rounded border border-gray-300 bg-white hover:bg-primary-50 hover:border-primary-300"
                      >
                        {`{{${v}}}`}
                      </button>
                    ))}
                  </div>
                </details>

                <Field label="Contenu HTML *">
                  <div className="bg-white rounded border border-gray-300">
                    <ReactQuill
                      theme="snow"
                      value={contenuHtml}
                      onChange={setContenuHtml}
                      modules={QUILL_MODULES}
                      style={{ minHeight: 250 }}
                    />
                  </div>
                </Field>

                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Template actif</span>
                </label>
              </form>
            )}

            {/* Preview */}
            {!loading && (
              <details className="mt-4 rounded-lg border border-gray-200">
                <summary
                  className="cursor-pointer px-3 py-2 bg-gray-50 text-sm font-medium text-gray-700 inline-flex items-center"
                  onClick={generatePreview}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Prévisualisation (données fictives)
                </summary>
                <div className="p-3">
                  <p className="text-xs text-gray-500 mb-2">
                    Sujet rendu : <strong>{previewSubject || '—'}</strong>
                  </p>
                  <div
                    className="border border-gray-200 rounded-lg p-3 bg-white"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        previewHtml || '<em>— Cliquez pour générer —</em>',
                      ),
                    }}
                  />
                </div>
              </details>
            )}

            {/* Test send */}
            {!loading && (
              <div className="mt-4 rounded-lg border border-gray-200 p-3 bg-gray-50">
                <p className="text-xs text-gray-600 mb-2">Envoyer un test à :</p>
                <div className="flex flex-wrap items-center space-x-2 space-y-2 sm:space-y-0">
                  <input
                    type="email"
                    value={testTo}
                    onChange={(e) => setTestTo(e.target.value)}
                    placeholder="destinataire@example.com"
                    className={inputClass + ' flex-1 min-w-[200px]'}
                  />
                  <button
                    type="button"
                    onClick={sendTest}
                    disabled={testSending || !testTo}
                    className="inline-flex items-center px-3 py-2 border border-primary-200 text-primary-700 bg-white rounded-lg text-sm font-medium hover:bg-primary-50 disabled:opacity-50"
                  >
                    {testSending ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-1" />
                    )}
                    Envoyer
                  </button>
                </div>
                {testResult && (
                  <p className="text-sm text-green-600 mt-2 inline-flex items-center">
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    {testResult}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end space-x-2 p-4 border-t border-gray-200 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              form="template-form"
              disabled={saving || loading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              {isCreate ? 'Créer le modèle' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

function buildFakeVariables(): Record<string, unknown> {
  return {
    user: {
      first_name: 'Marie',
      last_name: 'Dubois',
      email: 'marie.dubois@example.com',
    },
    temporary_password: 'Aa9!demoPassword',
    login_url: 'https://mygmao.pro/login',
    reset_url: 'https://mygmao.pro/reset-password?token=abc123',
    action_url: 'https://mygmao.pro/dashboard',
    subject: "Sujet d'exemple",
    message: "Message d'exemple pour la prévisualisation.",
    recipient_email: 'destinataire@example.com',
    timestamp: new Date().toISOString(),
  };
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}
